import { Link, useParams } from "react-router-dom";
import {
    completeRequestShareFile,
    createFolder,
    deleteFolder,
    getRequestShareFileInfo,
    shareFolder,
    uploadFile
} from "../../../lib/services/drive";
import { useEffect, useState } from "react";
import { Alert, Box, Button, Container, Divider, Fade, Grid, Skeleton, styled, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import {
    AllInclusiveOutlined,
    ArticleRounded,
    CalendarMonthRounded,
    ContactSupport,
    GppGoodOutlined,
    InfoRounded,
    LockOutlined,
    PaymentsOutlined
} from "@mui/icons-material";
import Toasty from "../../../theme/components/Toasty";
import { farsiDate, farsiHour } from "../../../lib/commonFunctions/dateConvertor";
import UploadInput from "../../../components/home/upload/uploadInput";
import UploadingFiles from "../../../components/home/upload/uploadingFiles";
import uploadingFiles from "../../../components/home/upload/uploadingFiles";
import LoginModal from "../../../components/common/loginModal";
import { useUser } from "../../../lib/hooks";

export default function RequestFileView() {

    const { linkAddress } = useParams();
    const [requestInfo, setRequestInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pending, setPending] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const user = useUser();
    const [loginModal, setLoginModal] = useState(false);

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadDone, setUploadDone] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [createdFolderId, setCreatedFolderId] = useState();

    const handleSetProgress = (payload, file, progress) => {
        const newSelectedFiles = [...payload];
        const fileIndex = newSelectedFiles.indexOf(file);
        if (fileIndex < 0) return;
        newSelectedFiles[fileIndex].progress = progress;
        setSelectedFiles(newSelectedFiles);
    }
    const handleAbort = (file) => {
        if (file.isCompleted) return;
        const newFiles = [...selectedFiles];
        const index = newFiles.indexOf(file);
        if (index < 0) return;
        const selectedFile = newFiles[index];
        selectedFile.controller.abort();
        selectedFile.isAborted = true;
        setSelectedFiles(newFiles);
    }
    const handleAbortAll = async (options) => {
        const { cleanup } = options;
        const newFiles = [...selectedFiles];
        for await (const file of newFiles) {
            if (!file.isCompleted) {
                await file.controller.abort();
                file.isAborted = true;
            }
        }
        if (!cleanup) setSelectedFiles(newFiles);
        else setSelectedFiles([]);
        if (createdFolderId) await deleteFolder(createdFolderId);
    }
    const handleUpload = async (files, isLoggedIn) => {
        if (!files?.length) return;
        if (!user && !isLoggedIn) {
            setSelectedFiles([...files]);
            return setLoginModal(true);
        }
        setUploadDone(false);
        setIsUploading(true);
        const uploadingFiles = [...files];
        uploadingFiles.forEach(file => {
            file.controller = new AbortController();
            file.isCompleted = false;
            file.progress = 0;
        });
        setSelectedFiles(uploadingFiles);
        const folderName = `shared-${new Date().getTime()}`
        const data = await createFolder(0, folderName);
        if (data?.isSuccess) {
            const folderId = data.value;
            setCreatedFolderId(folderId);
            // This "for" method will upload files one by one
            // if you want multiple uploads at the same time use forEach method.
            const uploaded = [];
            for await (const file of uploadingFiles) {
                const response = await uploadFile(file, (p) => handleSetProgress(uploadingFiles, file, p), folderId); // 0 is ShareFolder
                const data = response?.data;
                const fileIndex = uploadingFiles.indexOf(file);
                if (data && data.isSuccess) {
                    if (fileIndex >= 0) {
                        uploadingFiles[fileIndex].isCompleted = true;
                        setSelectedFiles(uploadingFiles);
                    }
                    if (data.value?.length) uploaded.push(...data.value);
                }
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i, { autoClose: false }));
                    if (fileIndex >= 0) {
                        uploadingFiles[fileIndex].isAborted = true;
                        uploadingFiles[fileIndex].errors = data.errors;
                        uploadingFiles[fileIndex].isCompleted = false;
                    }
                    return await deleteFolder(folderId);
                }
            }
            setUploadedFiles(uploaded);
            setSelectedFiles(uploadingFiles);
            setIsUploading(false);
        } else {
            if (data && data.errors?.length) {
                data.errors.map(i => Toasty.error(i));
            }
        }

    }

    async function completeShareRequest() {
        if (!uploadingFiles.length) return;
        const Ids = uploadedFiles.map(i => i.fileId);
        if (!Ids.length) return;
        setPending(true);
        try {
            const { data } = await completeRequestShareFile(linkAddress, Ids);
            if (data?.isSuccess) {
                setUploadDone(true);
                if (data.successMessage) Toasty.success(data.successMessage);
            } else {
                setPending(false);
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i, { autoClose: false }));
                    const newFiles = [...selectedFiles];
                    newFiles.forEach(file => {
                        file.isCompleted = false
                        file.isAborted = true;
                    });
                    setSelectedFiles(newFiles);
                    return await deleteFolder(createdFolderId);
                }
            }
        } catch (err) {
            console.log(err);
            Toasty.error('خطایی غیر منتظره رخ داد')
        }
        setPending(false);
    }
    // this makes sure everything goes right
    useEffect(() => {
        if (selectedFiles?.length) {
            const unCompletedFiles = selectedFiles.filter(f => f.isCompleted === false);
            // All Files Uploaded
            if (!unCompletedFiles?.length) {
                completeShareRequest();
            }
        }
    }, [selectedFiles])


    // Get Initial Data
    async function getShareInfo() {
        try {
            const { data } = await getRequestShareFileInfo(linkAddress);
            if (data?.isSuccess) {
                setRequestInfo(data.value);
            } else {
                setNotFound(true);
                if (data && data.errors?.length) {
                    data.errors.map(i => Toasty.error(i));
                }
            }
            setLoading(false);
        } catch (err) {
            console.log(err);
            Toasty.error('خطایی غیر منتظره رخ داد')
        }
    }
    useEffect(() => {
        getShareInfo();
    }, []);

    return (
        <>
            <Helmet>
                <title>درخواست اشتراک گذاری - میلفا</title>
            </Helmet>
            {notFound && !loading
                ?
                <Container sx={{ mt: 3, mb: 15 }}>
                    <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                        <Box component="img" src="/assets/image/icon/404.svg" sx={{ width: { xs: 256, lg: 512 }, height: { xs: 256, lg: 512 }, objectFit: "contain" }} />
                        <Typography variant="h1" component="p" fontSize={{ xs: "1.1rem", lg: "1.25rem" }} fontWeight={700}>خطای 404</Typography>
                        <Typography mt={1.5} fontSize={{ xs: ".985rem", lg: "1rem" }} fontWeight={500}>درخواست اشتراک گذاری مورد نظر پیدا نشد!</Typography>
                        <Link to="/">
                            <Typography mt={1.5} fontSize=".975rem" color="primary">بازگشت به صفحه اصلی</Typography>
                        </Link>
                    </Box>
                </Container>
                :
                <>
                    <Container sx={{ mt: 3 }}>
                        <Typography variant="h1" fontSize={{ xs: "1.15rem", lg: "1.25rem" }} fontWeight={700}>درخواست ارسال اشتراک گذاری</Typography>
                        <Grid mt={0} container spacing={5}>
                            <Grid item xs={12}>
                                <Grid item xs={12} lg={6} xl={5}>
                                    {!loading
                                        ?
                                        <Alert severity="info" sx={{ display: "inline-flex" }}>
                                            <Box display='flex' alignItems="baseline">
                                                <Typography fontWeight={600}>{requestInfo?.senderFirstName ? `${requestInfo.senderFirstName}  ${requestInfo.senderLastName}` : 'کاربر نامشخص'}</Typography>
                                                <Typography ml={.85}>از شما درخواست اشتراک گذاری دارد</Typography>
                                            </Box>
                                            <Divider sx={{ my: 1.5, borderColor: "inherit", opacity: .2 }} />
                                            <Box display='flex' alignItems="center">
                                                <InfoRounded fontSize="small" />
                                                <Typography ml={.7} fontWeight={500}><strong style={{ fontWeight: 400 }}>عنوان:</strong> {requestInfo?.title ? requestInfo.title : "بدون عنوان"}</Typography>
                                            </Box>
                                            <Box mt={1.5} display='flex' alignItems="center">
                                                <ArticleRounded fontSize="small" />
                                                <Typography ml={.7} fontWeight={500}><strong style={{ fontWeight: 400 }}>توضیحات:</strong> {requestInfo?.description ? requestInfo.description : "بدون توضیح"}</Typography>
                                            </Box>
                                            <Box mt={1.5} display='flex' alignItems="center">
                                                <CalendarMonthRounded fontSize="small" />
                                                <Typography ml={.7} fontWeight={500}><strong style={{ fontWeight: 400 }}>تاریخ ایجاد:</strong> {requestInfo.createDate ? `${farsiHour(requestInfo.createDate)} - ${farsiDate(requestInfo.createDate)}` : "نامشخص"}</Typography>
                                            </Box>
                                        </Alert>
                                        :
                                        <Skeleton variant="rounded" width="100%" height="15rem" />
                                    }
                                </Grid>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <UploadInput onChange={handleUpload} disabled={isUploading || loading} />
                            </Grid>
                            <Fade in={!!selectedFiles.length} unmountOnExit>
                                <Grid item xs>
                                    <UploadingFiles
                                        files={selectedFiles}
                                        setFiles={setSelectedFiles}
                                        onAbort={handleAbort}
                                        onAbortAll={handleAbortAll}
                                        isUploading={isUploading}
                                        uploadDone={uploadDone}
                                        generatedLink={linkAddress}
                                        pending={pending} />
                                </Grid>
                            </Fade>
                            <Grid item xs={12} lg={6}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={7}>
                                        <Typography fontSize={{ xs: "1.1rem", lg: "1.2rem" }} fontWeight={600}>میلفا چیست؟!</Typography>
                                        <Typography textAlign="justify" sx={{ lineHeight: 1.725 }}>
                                            لورم ایپسوم متن ساختگی با تولید سادگی نامفهوم از صنعت چاپ و با استفاده از طراحان گرافیک است. چاپگرها و متون بلکه روزنامه و مجله در ستون و سطرآنچنان که لازم است و برای شرایط فعلی تکنولوژی مورد نیاز و کاربردهای متنوع با هدف بهبود ابزارهای کاربردی می باشد. کتابهای زیادی در شصت و سه درصد گذشته، حال و آینده شناخت فراوان جامعه و متخصصان را می طلبد تا با نرم افزارها شناخت بیشتری را برای طراحان رایانه ای علی الخصوص طراحان خلاقی و فرهنگ پیشرو در زبان فارسی ایجاد کرد.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} lg={5}>
                                        <Box component="img" src="/assets/image/icon/storage-cloud.svg" width={1} height={1} sx={{ maxHeight: "50vw", objectFit: "contain" }} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Link to="/about-us">
                                            <Button fullWidth variant="contained" sx={{ py: 1.5 }} disableElevation>اطلاعات بیشتر</Button>
                                        </Link>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Container>
                    <Container sx={{ my: 12 }}>
                        <Box display="flex" alignItems="center">
                            <ContactSupport color="primary" fontSize="large" />
                            <Typography ml={1} variant="h6" fontSize={{ xs: "1rem", lg: "1.2rem" }} fontWeight={600}>مزایای استفاده از میلفا</Typography>
                        </Box>
                        <Box mt={3}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} lg={3}>
                                    <Feature
                                        title="امنیت بالا"
                                        description="دارای چندین لایه امنیتی و استفاده از به روز ترین متد های امنیتی."
                                        color="#388E3C"
                                        icon={<GppGoodOutlined sx={{ fontSize: "2.25rem" }} />} />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <Feature
                                        title="پهنای باند نامحدود"
                                        description="بدون نگرانی از دچار افت سرعت فایل های خودتون رو دانلود یا آپلود کنید."
                                        color="#3949AB"
                                        icon={<AllInclusiveOutlined sx={{ fontSize: "2.25rem" }} />} />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <Feature
                                        title="قابلیت فروش فایل"
                                        description="آپلود فایل های اختصاصی فروشی، بعد از پرداخت مبلغ مورد نظر شما دسترسی به فایل خواهید داشت."
                                        color="#FF6D00"
                                        icon={<PaymentsOutlined sx={{ fontSize: "2.25rem" }} />} />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <Feature
                                        title="خصوصی سازی"
                                        description="با استفاده از قابلیت رمز گذاری دسترسی فایل های خود را برای دیگران محدود کنید."
                                        color="#0277BD"
                                        icon={<LockOutlined sx={{ fontSize: "2.25rem" }} />} />
                                </Grid>
                            </Grid>
                        </Box>
                    </Container>
                </>
            }
            <LoginModal
                open={loginModal}
                onClose={() => setLoginModal(false)}
                options={{ noReload: true, onSuccess: () => handleUpload(selectedFiles, true) }} />
        </>
    )
}

const Feature = (props) => {

    const { title, description, icon, color, ...restProps } = props;

    const Root = styled(Box)(({ theme }) => ({
        position: "relative",
        height: "100%",
        padding: 16,
        borderRadius: 8,
        background: "#fff",
        color: color,
        boxShadow: "3px 6px 16px -6px rgb(66 66 66 / 25%)",
        [theme.breakpoints.up('lg')]: {
            padding: 24,
        },
        "&:before": {
            content: "''",
            display: "block",
            width: 5,
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            background: color,
            borderRadius: "inherit"
        }
    }));

    return (
        <Root {...restProps}>
            {icon}
            <Typography fontSize={{ xs: "1.05rem" }} fontWeight={600}>{title}</Typography>
            <Typography mt={.5} color='initial' fontSize=".925rem" textAlign="justify">{description}</Typography>
        </Root>
    )
}
